import { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';

import { createMapAdapter, type MapAdapter } from './map-adapter';
import type { MapViewSnapshot } from './map-camera-controller';
import { prepareMapterhornContours } from './terrain-contour-plugin';
import { measureRadarCoverage, sampleRadarRoutes } from './weather-radar-sampling';
import { prepareWindPlugins } from './weather-wind-runtime';

const BASE = import.meta.env.BASE_URL || '/map/';
const asset = (path: string) => `${BASE}${path.replace(/^\//, '')}`;

export interface InitialMapView {
  center: [number, number];
  zoom: number;
  bearing: number;
  pitch: number;
}

export interface MapCanvasProps {
  initialStyle: string;
  styleUrl?: string;
  initialView: InitialMapView;
  theme: string;
  onAdapter?: (adapter: MapAdapter | null) => void;
  onReady?: () => void;
  onMove?: (view: MapViewSnapshot) => void;
  onMoveEnd?: (event: { view: MapViewSnapshot; hash: string }) => void;
  onZoomEnd?: (event: { view: MapViewSnapshot; hash: string }) => void;
  onIdle?: () => void;
  onBeforeRemove?: () => void;
  classifyRadarColor?: (red: number, green: number, blue: number, alpha: number) => number;
}

export function MapCanvas(props: MapCanvasProps) {
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const adapterRef = useRef<MapAdapter | null>(null);
  const runtimeRef = useRef<any>(null);
  const lastWorkingStyleRef = useRef<string | null>(null);
  const pendingStyleRef = useRef<string | null>(props.initialStyle);
  const failedStyleRef = useRef<string | null>(null);
  const fallbackLoadingRef = useRef(false);
  const rawSetStyleRef = useRef<((styleUrl: string) => void) | null>(null);
  const [styleFailure, setStyleFailure] = useState<string | null>(null);
  const callbacksRef = useRef(props);
  callbacksRef.current = props;

  useEffect(() => {
    if (!nodeRef.current || runtimeRef.current) return;
    const runtime = new maplibregl.Map({
      container: nodeRef.current,
      style: props.initialStyle,
      center: props.initialView.center,
      zoom: props.initialView.zoom,
      bearing: props.initialView.bearing,
      pitch: props.initialView.pitch,
      minZoom: 2,
      projection: { type: 'globe' },
      attributionControl: false,
      maplibreLogo: false
    } as any);
    const installPluginRuntime = () => { (window as any).maplibregl = maplibregl; };
    const adapter = createMapAdapter(runtime as any, {
      runtimeBindings: {
        createMarker: (map, element, coordinate) => new maplibregl.Marker({ element }).setLngLat(coordinate).addTo(map as any) as any,
        showPopup: (map, coordinate, html, offset) => {
          new maplibregl.Popup({ offset }).setLngLat(coordinate).setHTML(html).addTo(map as any);
        }
      },
      weather: {
        measureRadarCoverage: props.classifyRadarColor
          ? (tileUrl, bounds) => measureRadarCoverage(tileUrl, bounds, props.classifyRadarColor!)
          : undefined,
        sampleRadarRoutes,
        prepareWind: () => prepareWindPlugins(installPluginRuntime, asset)
      },
      terrain: {
        prepareContours: () => prepareMapterhornContours(
          installPluginRuntime,
          (source) => (source as any).setupMaplibre(maplibregl)
        )
      }
    });
    const rawSetStyle = adapter.setStyle.bind(adapter);
    rawSetStyleRef.current = rawSetStyle;
    adapter.setStyle = (styleUrl: string) => {
      pendingStyleRef.current = styleUrl;
      failedStyleRef.current = null;
      fallbackLoadingRef.current = false;
      setStyleFailure(null);
      rawSetStyle(styleUrl);
    };
    runtimeRef.current = runtime;
    adapterRef.current = adapter;
    (runtime as any).__beachTheme = props.theme;
    if (import.meta.env.DEV) (window as any).__beachMap = runtime;
    callbacksRef.current.onAdapter?.(adapter);

    const handlers = {
      load: () => callbacksRef.current.onReady?.(),
      move: () => callbacksRef.current.onMove?.(adapter.camera.viewSnapshot()),
      moveend: () => callbacksRef.current.onMoveEnd?.({ view: adapter.camera.viewSnapshot(), hash: adapter.camera.hash() }),
      zoomend: () => callbacksRef.current.onZoomEnd?.({ view: adapter.camera.viewSnapshot(), hash: adapter.camera.hash() }),
      idle: () => callbacksRef.current.onIdle?.(),
      'style.load': () => {
        if (pendingStyleRef.current) lastWorkingStyleRef.current = pendingStyleRef.current;
        pendingStyleRef.current = null;
        if (fallbackLoadingRef.current) {
          fallbackLoadingRef.current = false;
        } else {
          failedStyleRef.current = null;
          setStyleFailure(null);
        }
      },
      error: (event?: { sourceId?: string; tile?: unknown; source?: unknown }) => {
        if (event?.sourceId || event?.tile || event?.source) return;
        const failedStyle = pendingStyleRef.current;
        if (!failedStyle) return;
        failedStyleRef.current = failedStyle;
        setStyleFailure(failedStyle);
        const fallbackStyle = lastWorkingStyleRef.current;
        if (fallbackStyle && fallbackStyle !== failedStyle) {
          pendingStyleRef.current = fallbackStyle;
          fallbackLoadingRef.current = true;
          rawSetStyle(fallbackStyle);
        }
      }
    };
    Object.entries(handlers).forEach(([event, handler]) => runtime.on(event, handler));

    return () => {
      callbacksRef.current.onBeforeRemove?.();
      callbacksRef.current.onAdapter?.(null);
      Object.entries(handlers).forEach(([event, handler]) => runtime.off(event, handler));
      adapter.dispose();
      runtime.remove();
      adapterRef.current = null;
      runtimeRef.current = null;
      rawSetStyleRef.current = null;
    };
  }, []);

  useEffect(() => {
    const runtime = runtimeRef.current;
    if (runtime) runtime.__beachTheme = props.theme;
  }, [props.theme]);

  useEffect(() => {
    if (props.styleUrl && props.styleUrl !== props.initialStyle) adapterRef.current?.setStyle(props.styleUrl);
  }, [props.initialStyle, props.styleUrl]);

  const retryStyle = () => {
    const failedStyle = failedStyleRef.current || styleFailure;
    if (!failedStyle || !rawSetStyleRef.current) return;
    pendingStyleRef.current = failedStyle;
    fallbackLoadingRef.current = false;
    rawSetStyleRef.current(failedStyle);
  };

  return (
    <>
      <div ref={nodeRef} className="map" />
      {styleFailure ? (
        <div className="map-style-error" role="alert">
          <span>Map style unavailable</span>
          <button type="button" onClick={retryStyle}>Retry</button>
        </div>
      ) : null}
    </>
  );
}
